import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
  keyframes,
} from '@mui/material';
import { IBurgerMenuItem } from 'API/Home/burgerMenuItems';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useImposter } from 'Hooks';
import { Home, PurpleBack, PurpleClose } from 'Images';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectedCategory } from './SelectedCategory';
import { User } from './User';
import { UpdatesAndPersonalDetails } from './UpdatesAndPersonalDetails';
import { CategoriesList } from './CategoriesList';
import { MenuOutro } from './MenuOutro';
import { getSupplierRedirectCode } from 'API/Treatment/treatment';
import { eshetTursRedirectCode, flyingCarpetRedirectCode } from 'Config';
import { getIsLawyer, getIsLawyerAndUser } from 'Utils/lawyers';

interface IMobileMenu {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
  showDialog: (showDialog: boolean) => void;
}

export const MobileMenu = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  showDialog,
}: IMobileMenu) => {
  const burgerMenuItems = useAppSelector(
    ({ menuItems: { menuItems } }) => menuItems
  );
  const [selectedCategory, setSelectedCategory] =
    useState<IBurgerMenuItem | null>(null);

  const dictionary = useDictionary(Dictionary, DICTIONARY.MobileMenu);
  const navigate = useNavigate();

  const [isSlideOut, setIsSlideOut] = useState<boolean>(false);

  const personalAndUpdatesPositionArray = 2;

  const onClickCategory = (category: IBurgerMenuItem) =>
    setSelectedCategory(category);

  useEffect(() => {
    if (!isMobileMenuOpen) setTimeout(() => setSelectedCategory(null), 500);
  }, [isMobileMenuOpen]);

  const imposter = useImposter();

  const isLawyer = getIsLawyerAndUser()
    ? sessionStorage.getItem('isLawyer') === 'true'
    : getIsLawyer();

  const itemNavigationHandler = async (item: IBurgerMenuItem) => {
    if (
      (getIsLawyerAndUser()
        ? sessionStorage.getItem('isLawyer') === 'true'
        : isLawyer) &&
      !imposter.imposterId
    ) {
      showDialog(true);
      return;
    }
    if (item.internalLink) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/${item.internalLink}`);
    } else if (item.externalLink) {
      let code = 0;
      let redirectUrl = '';
      let urlParams = '';
      if (item.externalLink === Dictionary[DICTIONARY.General].eshetTursUrl) {
        code = eshetTursRedirectCode;
        redirectUrl = Dictionary[DICTIONARY.General].eshetTursUrl;
        urlParams = '/?segmentId=18&subSegmentId=1&auth=';
      } else if (
        item.externalLink === Dictionary[DICTIONARY.General].flyingCarpetUrl
      ) {
        code = flyingCarpetRedirectCode;
        redirectUrl = Dictionary[DICTIONARY.General].flyingCarpetUrl;
        urlParams = '/?code=';
      }

      if (code !== 0) {
        const newWindow = window.open();
        const redirectCode = await getSupplierRedirectCode(code);

        if (redirectCode && newWindow) {
          newWindow.location = `${redirectUrl}${urlParams}${encodeURIComponent(
            redirectCode
          )}`;
        } else newWindow?.close();
      } else window.open(item.externalLink, '_blank');
    } else {
      setIsSlideOut(false);
      onClickCategory(item);
    }
    if (item.internalLink || item.externalLink) setIsMobileMenuOpen(false);
  };

  const slideIn = keyframes`
    from{
      transform: translateX(-100%);
      opacity: 0;
    }
    to{
      transform: translateX(0);
      opacity: 1;
    }
  `;

  const slideOut = keyframes`
    from{}
    to{
      transform: translateX(-100%);
      opacity: 0;
    }
  `;

  const getMenuItems = () =>
    [...burgerMenuItems].slice(isLawyer ? 0 : personalAndUpdatesPositionArray);

  return (
    <>
      <Stack
        width={'94vw'}
        height={'80vh'}
        bgcolor={'white'}
        position={'absolute'}
        left={isMobileMenuOpen ? 0 : '-94vw'}
        top={'62px'}
        p={'50px 32px 20px 24px'}
        gap={'8px'}
        zIndex={2}
        sx={{
          transition: 'left 0.5s',
          borderBottomRightRadius: '30px',
          overflowY: 'scroll',
          overflowX: 'hidden',
          animation: `${slideIn} .25s ease-out`,
          m: 0,
        }}
        component='menu'
      >
        {selectedCategory !== null && (
          <IconButton
            onClick={() => {
              setIsSlideOut(true);
              setTimeout(() => {
                setSelectedCategory(null);
              }, 350);
            }}
            sx={{ position: 'absolute', top: 0, left: 0 }}
          >
            <Avatar
              src={PurpleBack}
              alt={Dictionary[DICTIONARY.General].back}
            />
          </IconButton>
        )}
        <IconButton
          onClick={() => setIsMobileMenuOpen(false)}
          sx={{ position: 'absolute', top: 0, right: 0 }}
        >
          <Avatar
            src={PurpleClose}
            alt={Dictionary[DICTIONARY.General].closeWindow}
          />
        </IconButton>
        {selectedCategory === null ? (
          <>
            <User />
            <Divider
              aria-hidden={true}
              sx={{ borderColor: 'primary.shade_100', margin: '6px 0 3px 0' }}
            />
            {!isLawyer && (
              <UpdatesAndPersonalDetails
                updatesAndPersonalDetails={
                  burgerMenuItems &&
                  burgerMenuItems?.slice(0, personalAndUpdatesPositionArray)
                }
                itemNavigationHandler={itemNavigationHandler}
              />
            )}
            <Stack mt={'34px'} gap={'32px'}>
              <Button
                sx={{
                  p: 0,
                  width: '100%',
                  justifyContent: 'flex-start',
                  gap: '10px',
                }}
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  navigate(isLawyer ? '/lawyers-lobby' : '/');
                }}
                role='link'
              >
                <Avatar
                  sx={{ width: '24px', height: '24px' }}
                  src={Home}
                  alt=''
                />
                <Typography variant='body1' color='initial' component='h3'>
                  {isLawyer ? dictionary.lawyerHome : dictionary.home}
                </Typography>
              </Button>
              <CategoriesList
                burgerMenuItems={getMenuItems()}
                itemNavigationHandler={itemNavigationHandler}
              />
            </Stack>
            <MenuOutro />
          </>
        ) : (
          <Box
            sx={{
              animation: isSlideOut
                ? `${slideOut} .35s linear`
                : `${slideIn} .3s ease-out`,
            }}
          >
            <SelectedCategory
              selectedCategory={selectedCategory}
              itemNavigationHandler={itemNavigationHandler}
            />
          </Box>
        )}
      </Stack>
      <Box
        onClick={() => setIsMobileMenuOpen(false)}
        sx={{
          width: '100vw',
          height: '100vh',
          position: 'absolute',
          left: isMobileMenuOpen ? 0 : '-100vw',
          top: '61px',
          bgcolor: 'secondary.main',
          opacity: isMobileMenuOpen ? '0.7' : '0',
          transition: 'opacity 0.3s',
        }}
      />
    </>
  );
};
